import { Severity } from '@sentry/types';
import { Location } from 'history';
import { get } from 'lodash';
import { Dispatch } from 'redux';

import {
  getCurrentCustomer,
  getCurrentCustomerLease,
} from '../../services/apply';
import {
  Customer,
  getCurrentUserInfo,
  LoginResponse,
  loginWithPin,
  SessionLogoutPayload,
  signUp,
  SignupResponse,
  UserInfo,
  UserPayload,
} from '../../services/auth';
import { captureException } from '../../services/sentry';
import {
  ActionInterface,
  dispatcher,
  generateActions,
} from '../../utils/redux';
import { SessionActions } from './SessionActions';

const sessionActionsMap: {
  [key: string]: SessionActions;
} = SessionActions as any;

export function logoutSession(
  payload: SessionLogoutPayload = {}
): ActionInterface<SessionActions, SessionLogoutPayload> {
  return {
    payload,
    type: SessionActions.deleteSession,
  };
}

export function setRedirectUrl(
  payload: Location
): ActionInterface<SessionActions, Location> {
  return {
    payload,
    type: SessionActions.setLoginRedirect,
  };
}

export function DoSetRedirectUrl(location: Location) {
  return (d: Dispatch<ActionInterface<SessionActions, Location>>) => {
    const dispatch = dispatcher(d as any);
    dispatch(setRedirectUrl)(location);
  };
}

export function DoCreateAccount(user: UserPayload) {
  const [start, success, error] = generateActions<
    SessionActions,
    SignupResponse
  >('createAccount', sessionActionsMap);
  return async (d: Dispatch<ActionInterface<SessionActions>>): Promise<any> => {
    const dispatch = dispatcher(d as any);
    dispatch(start)();
    try {
      const response = await signUp(user);
      return dispatch(success)(response);
    } catch (e) {
      const errors = captureException(e);
      dispatch(error)(errors.toString());
      throw e;
    }
  };
}

export function setCurrentUser(user: Partial<Customer>) {
  return (d: Dispatch<ActionInterface<SessionActions>>) => {
    const [, success] = generateActions<SessionActions, SignupResponse>(
      'createAccount',
      sessionActionsMap
    );
    const dispatch = dispatcher(d as any);
    dispatch(success)({ user });
  };
}

export function DoPinLogin(phone: string, pin: string) {
  return async (
    d: Dispatch<ActionInterface<SessionActions>>
  ): Promise<LoginResponse> => {
    const [start, success, error] = generateActions(
      'pinLogin',
      sessionActionsMap
    );
    const dispatch = dispatcher(d as any);
    dispatch(start)({});
    try {
      const result = await loginWithPin(phone, pin);
      dispatch(success)(result);
      return result;
    } catch (e) {
      captureException(e, Severity.Warning);
      dispatch(error)(e.errors);
      throw e;
    }
  };
}

export function DoGetCurrentUserInfo() {
  return async (d: Dispatch<ActionInterface<SessionActions>>) => {
    const [getInfoStart, getInfoSuccess, getInfoError] = generateActions(
      'getUserInfo',
      sessionActionsMap
    );
    const dispatch = dispatcher(d);
    dispatch(getInfoStart)({});
    try {
      const { user } = await getCurrentUserInfo();
      const currentLease = await getCurrentCustomerLease(user._id);
      const currentCustomer = await getCurrentCustomer(user._id);
      let currentAddress = get(currentLease, 'location', undefined);
      if (!currentAddress) {
        currentAddress = get(currentCustomer, 'location');
      }
      const userInfo: UserInfo = {
        admin: user.admin,
        currentAddress,
        currentLease,
        dob: get(currentCustomer, 'dob'),
        email: user.email,
        firstName: user.first_name,
        id: user._id,
        lastName: user.last_name,
        phone: user.phone,
        roles: user.roles,
      };
      dispatch(getInfoSuccess)({ userInfo });
      return userInfo;
    } catch (e) {
      const errors = captureException(e, Severity.Warning);
      dispatch(getInfoError)(errors.toString());
      throw e;
    }
  };
}
